import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { Text } from '@lmn/design-system';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { getTransportDistance, mapTransportDistance } from '@/app/components/hotel-detail/common/transportDistance';
import { useMetaReview } from '@/app/components/hotel-detail/hooks/useMetaReview';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { isTrustYouSource } from '@/app/common/reviews/reviewSource';
import { getDistanceFromM } from '@/app/common/measures/getters.ts';
import { Icon } from '@/app/common/icons/Icon';
import { HeroAddress } from './HeroAddress';
import { useIsMobile } from './hooks/useMediaSSR';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

type HeroHotelLocationProps = {
  distanceToCityCenter?: number;
  city?: string;
  openMap: () => void;
} & React.HTMLAttributes<HTMLElement>;

// #region styled components
const BannerWithDistances = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 8px;
  min-height: 76px;
  height: fit-content;
  padding: 16px;
  width: 100%;
`;

const DistanceTextContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 0;

  /* Requested at the 20px drawn from sm up, so it is shrunk rather than upscaled below it. */
  img {
    width: 16px;
    height: 16px;
  }

  @media ${customCss.breakpoints.sm} {
    padding: 4px 0;

    img {
      width: 20px;
      height: 20px;
    }
  }
`;

const IconWrap = styled.div`
  display: flex;
  font-size: ${(p) => p.theme.commons.font.sizes.base};
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

const DisclaimerText = styled(Text).attrs({ variant: 'Caption', color: 'textSecondary' })`
  margin-top: 8px;
`;

const Separator = styled.hr`
  margin: 16px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
  width: 100%;
`;
// #endregion

export function HeroHotelLocation(props: HeroHotelLocationProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { openMap, distanceToCityCenter, city, ...rest } = props;
  const { commonData, localization } = usePageWrapperData();
  const { accommodationOnly } = useUrlSearchParams();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { t } = useTranslation();
  const { reviewsDisabled } = localization.configValuesApp;
  // Only fetch the TrustYou meta-review (used for the "excellent location" badge) for an explicit
  // TrustYou hotel — not for TA/unknown/absent/Expedia sources.
  const isTrustYou = isTrustYouSource(hotelStaticData?.hotelOpinion.reviewSource);
  const { metaReview } = useMetaReview(
    isTrustYou && !reviewsDisabled ? hotelStaticData?.hotelOpinion.opinionId : undefined,
    localization.lang
  );

  const isExcellentLocation = (metaReview?.categories?.find((category) => category.id === '14')?.score ?? 0) > 87;
  const iconSize = 20;

  const { data: transportData } = useSelectedTransport();
  const distanceToHotelData = getTransportDistance(transportData);

  const getDistanceToTransport = () => {
    let distanceToTransport: TransportDistance | null | undefined;
    if (transportData) {
      const distancesData = mapTransportDistance(distanceToHotelData);
      const destinationCode = transportData?.transports?.[0]?.outbound?.destination;
      distanceToTransport = distancesData ? distancesData.find((distance) => distance.code === destinationCode) : null;
    }
    return distanceToTransport;
  };
  const distanceToTransport = getDistanceToTransport();

  const BannerWithDistancesComponent = BannerWithDistances;

  return accommodationOnly || distanceToCityCenter || distanceToTransport ? (
    <BannerWithDistancesComponent {...rest}>
      {isMobile ? (
        <>
          {hotelStaticData?.direction && <HeroAddress direction={hotelStaticData.direction} />}
          <Separator />
        </>
      ) : undefined}
      <div
        css={`
          display: flex;
          flex-direction: column;
          gap: 2px;

          @media ${customCss.breakpoints.sm} {
            gap: 0;
          }
        `}
      >
        {distanceToCityCenter && (
          <DistanceTextContainer>
            <IconWrap>
              <Icon category="Location" name="icon-flag-point" size={iconSize} color="iconSecondary" alt="" />
            </IconWrap>
            <Text variant="BodySmall">
              {t(
                city
                  ? 'vacation.select.details.distancehotelcitycenter2.title'
                  : 'vacation.select.details.distancehotelcitycenter.title',
                {
                  distance: getDistanceFromM(distanceToCityCenter, localization).value?.toString(),
                  unit: getDistanceFromM(distanceToCityCenter, localization).unit,
                  city: city || '',
                }
              )}
            </Text>
          </DistanceTextContainer>
        )}
        {accommodationOnly && isExcellentLocation && (
          <DistanceTextContainer>
            <IconWrap>
              <Icon category="Location" name="icon-pin-point-star" size={iconSize} color="iconSecondary" alt="" />
            </IconWrap>
            <Text variant="BodySmall">{t('vacation.select.details.excellent_location')}</Text>
          </DistanceTextContainer>
        )}
        {!accommodationOnly && <DistanceToTransport distanceToTransport={distanceToTransport} iconSize={iconSize} />}
      </div>
      <DisclaimerText>{t('vacation.select.details.distances.disclaimer')}</DisclaimerText>
    </BannerWithDistancesComponent>
  ) : null;
}

type DistanceToTransportProps = {
  distanceToTransport: TransportDistance | null | undefined;
  iconSize: number;
} & React.HTMLAttributes<HTMLElement>;

function DistanceToTransport(props: DistanceToTransportProps) {
  const { distanceToTransport, iconSize } = props;
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();

  return distanceToTransport?.distanceWithUnit?.value && distanceToTransport?.distanceWithUnit?.unit ? (
    <DistanceTextContainer>
      {isTrain ? (
        <IconWrap>
          <Icon category="Transportation" name="icon-train" size={iconSize} color="iconSecondary" alt="" />
        </IconWrap>
      ) : (
        <IconWrap>
          <Icon category="Transportation" name="icon-airplane-up-right" size={iconSize} color="iconSecondary" alt="" />
        </IconWrap>
      )}
      <Text variant="BodySmall">
        {t(
          isTrain
            ? 'vacation.select.details.distancestationhotel.title'
            : 'vacation.select.details.distanceairporthotel.title',
          {
            distance: distanceToTransport.distanceWithUnit?.value?.toString(),
            unit: distanceToTransport.distanceWithUnit?.unit,
          }
        )}
      </Text>
    </DistanceTextContainer>
  ) : null;
}
